import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ServiceStatusAction } from '../../actions';
import { ServiceModalAction } from '../../actions/ServiceModalAction';
import { Translate as t } from '../../config/Translate';
import { ENV_DEVELOPMENT } from '../../config/appConstants';
import { isAdminPathname } from '../../lib/isAdminPathname';

const POLLING_INTERVAL = 30000; // 30초

const pulseKeyframes = `
@keyframes serviceBadgePulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.4; }
}
`;

const ServiceStatusBadge = ({ enabled, level1, level2, level3, wsConnected, GetCounts, GetIssueManagers, issueManagerIds, issueManagersLoaded, countsLoaded, userAdminId, companyId }) => {
	const level1Count = level1?.count || 0;
	const level2Count = level2?.count || 0;
	const level3Count = level3?.count || 0;
	const history = useHistory();
	const location = useLocation();
	const timerRef = useRef(null);

	const isAdminPath = isAdminPathname(location.pathname);

	// 카운트 조회 + 폴링
	useEffect(() => {
		if (isAdminPath) {
			GetCounts();
			if (!wsConnected) {
				timerRef.current = setInterval(() => {
					GetCounts();
				}, POLLING_INTERVAL);
			}
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- GetCounts is stable ref from mapDispatch
	}, [isAdminPath, wsConnected]);

	// BUG-056: Issue Manager 목록 조회 — companyId 로드 후 재실행되도록 의존성 분리
	useEffect(() => {
		if (isAdminPath && companyId) {
			GetIssueManagers(companyId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- GetIssueManagers is stable ref from mapDispatch
	}, [isAdminPath, companyId]);

	// Admin 경로 아니면 숨김
	if (!isAdminPath) return null;

	// BUG-056: API 응답 전(로딩 중)에는 숨김 판단을 보류
	if (!countsLoaded || !issueManagersLoaded) return null;

	// Feature Flag Off면 숨김 (dev 환경은 강제 표시)
	if (!enabled && process.env.NODE_ENV !== ENV_DEVELOPMENT) return null;

	// Issue Manager 만 노출
	const isIssueManager = issueManagerIds.includes(userAdminId);
	if (!isIssueManager) return null;

	const hasAny = (level1Count + level2Count + level3Count) > 0;

	const handleClick = () => {
		history.push('/Admin/AdminServiceStatus');
	};

	const badgeStyle = (color, count) => ({
		display: 'inline-flex',
		alignItems: 'center',
		padding: '3px 8px',
		borderRadius: '12px',
		fontSize: '12px',
		fontWeight: 'bold',
		marginRight: '6px',
		cursor: 'pointer',
		backgroundColor: count > 0 ? color : '#6c757d50',
		color: '#fff',
		opacity: count > 0 ? 1 : 0.5,
		border: count > 0 ? `1px solid ${color}` : '1px solid transparent',
		textShadow: count > 0 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
		letterSpacing: '0.5px',
	});

	return (
		<>
			{hasAny && <style>{pulseKeyframes}</style>}
			<div onClick={handleClick} style={{
				display: 'inline-flex',
				alignItems: 'center',
				cursor: 'pointer',
				marginLeft: '10px',
				animation: hasAny ? 'serviceBadgePulse 2s ease-in-out infinite' : 'none',
			}}
				title={t('service.status.title')}>
				<span style={badgeStyle('#dc3545', level1Count)} title={t('service.status.badgeTooltipLv1')}>
					Lv1 &nbsp;{level1Count}
				</span>
				<span style={badgeStyle('#fd7e14', level2Count)} title={t('service.status.badgeTooltipLv2')}>
					Lv2 &nbsp;{level2Count}
				</span>
				<span style={badgeStyle('#ffc107', level3Count)} title={t('service.status.badgeTooltipLv3')}>
					Lv3 &nbsp;{level3Count}
				</span>
			</div>
		</>
	);
};

const mapState = (state) => ({
	enabled: state.ServiceStatusReducer.enabled,
	level1: state.ServiceStatusReducer.level1,
	level2: state.ServiceStatusReducer.level2,
	level3: state.ServiceStatusReducer.level3,
	wsConnected: state.ServiceStatusReducer.wsConnected,
	countsLoaded: state.ServiceStatusReducer.countsLoaded,
	issueManagerIds: state.ServiceModalReducer.issueManagerIds,
	issueManagersLoaded: state.ServiceModalReducer.issueManagersLoaded,
	userAdminId: state.AuthReducer?.user?.adminid || '',
	companyId: state.AuthReducer?.user?.companyId || '',
});

const mapDispatch = (dispatch) => ({
	GetCounts: () => dispatch(ServiceStatusAction.GetCounts()),
	GetIssueManagers: (companyId) => dispatch(ServiceModalAction.GetIssueManagers(companyId)),
});

export default connect(mapState, mapDispatch)(ServiceStatusBadge);
